import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { facilityData } from '../data/facilityData';
import { Maximize2, X, Check, Shield } from 'lucide-react';

export default function FacilityGallery({ onOpenConsultation }) {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [activeImageModal, setActiveImageModal] = useState(null);

  useEffect(() => {
    if (activeImageModal) {
      document.body.style.overflow = 'hidden';
      const handleKeyDown = (e) => {
        if (e.key === 'Escape') setActiveImageModal(null);
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        document.body.style.overflow = 'unset';
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [activeImageModal]);

  const categories = [
    'All',
    'Living Spaces',
    'Care & Supervision',
    'Daily Living',
    'Hygiene & Comfort',
    'Utilities & Safety',
    'Recreation & Wellness',
    'Cognitive & Library',
    'Support Services'
  ];

  const filteredItems = selectedCategory === 'All'
    ? facilityData
    : facilityData.filter(item => item.category === selectedCategory);

  return (
    <div className="space-y-10">
      
      {/* Category Filter Pills */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 no-scrollbar">
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setSelectedCategory(cat)}
            className={`px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all duration-200 ${
              selectedCategory === cat
                ? 'bg-[#0F766E] text-white shadow-sm'
                : 'bg-white text-[#18323A] hover:bg-[#F5FAF9] border border-[#D9E8E5]'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Grid of Facility Spaces */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filteredItems.map((item) => (
          <div
            key={item.id}
            className="group bg-white rounded-2xl border border-[#D9E8E5] overflow-hidden shadow-card hover:shadow-elevated transition-all duration-300 flex flex-col justify-between"
          >
            <div>
              {/* Image Container with Zoom & Fullscreen overlay */}
              <div className="relative h-64 overflow-hidden bg-[#123B5D] cursor-pointer" onClick={() => setActiveImageModal(item)}>
                <img
                  src={item.image}
                  alt={item.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-[#123B5D]/20 group-hover:bg-[#123B5D]/40 transition-colors"></div>
                
                <div className="absolute top-4 left-4">
                  <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-[#0F766E]/90 text-white backdrop-blur-sm border border-white/20">
                    {item.category}
                  </span>
                </div>

                <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="p-2 rounded-full bg-white/90 text-[#123B5D] shadow-md">
                    <Maximize2 className="w-4 h-4" />
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="p-6">
                <h3 className="font-serif text-xl sm:text-2xl text-[#123B5D] font-normal mb-2">
                  {item.title}
                </h3>
                <p className="text-xs sm:text-sm text-[#64748B] leading-relaxed mb-4">
                  {item.shortDescription}
                </p>

                <div className="space-y-2 pt-2 border-t border-[#D9E8E5]">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-[#18323A] block">
                    Designed Standards:
                  </span>
                  {item.features.map((feat, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-xs text-[#64748B]">
                      <Check className="w-3.5 h-3.5 text-[#0F766E] flex-shrink-0 mt-0.5" />
                      <span className="leading-snug">{feat}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Fullscreen Lightbox Modal Teleported to document.body */}
      {activeImageModal && typeof document !== 'undefined' && createPortal(
        <div
          style={{ zIndex: 99999 }}
          className="fixed inset-0 flex items-center justify-center p-4 sm:p-6 bg-black/85 backdrop-blur-md overflow-y-auto"
          onClick={() => setActiveImageModal(null)}
        >
          <div
            className="relative max-w-xl w-full bg-[#123B5D] rounded-2xl overflow-hidden shadow-2xl border border-white/10 my-auto max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Prominent Close Button */}
            <button
              onClick={() => setActiveImageModal(null)}
              style={{ zIndex: 100000 }}
              className="absolute top-3.5 right-3.5 p-2 rounded-full bg-[#0D273E] text-white hover:bg-[#0F766E] border border-white/20 shadow-xl transition-all hover:scale-105 cursor-pointer"
              aria-label="Close image modal"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Image Preview */}
            <div className="relative h-48 sm:h-56 w-full flex-shrink-0 bg-[#0D273E] overflow-hidden">
              <img
                src={activeImageModal.image}
                alt={activeImageModal.title}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Content Body with Scroll */}
            <div className="p-5 sm:p-6 text-white overflow-y-auto space-y-4">
              <div className="flex items-center gap-2">
                <Shield className="w-3.5 h-3.5 text-[#2A9D8F]" />
                <span className="text-[11px] uppercase tracking-widest text-[#2A9D8F] font-semibold">
                  {activeImageModal.category}
                </span>
              </div>

              <h3 className="font-serif text-xl sm:text-2xl text-white font-normal">
                {activeImageModal.title}
              </h3>

              <p className="text-xs sm:text-sm text-[#D9E8E5] leading-relaxed">
                {activeImageModal.shortDescription}
              </p>

              <div className="pt-3 border-t border-white/10">
                <span className="text-[11px] font-bold uppercase tracking-wider text-[#2A9D8F] block mb-2">
                  Key Features & Standards:
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {activeImageModal.features.map((feat, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-xs text-[#D9E8E5]">
                      <Check className="w-3.5 h-3.5 text-[#2A9D8F] flex-shrink-0 mt-0.5" />
                      <span className="leading-snug">{feat}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Bottom Action Footer */}
              <div className="pt-4 border-t border-white/10 flex items-center justify-between gap-3">
                <button
                  onClick={() => setActiveImageModal(null)}
                  className="px-4 py-2 text-xs font-semibold text-[#D9E8E5] hover:text-white bg-[#0D273E] hover:bg-[#0F766E] rounded-xl border border-white/10 transition-colors"
                >
                  Close
                </button>
                {onOpenConsultation && (
                  <button
                    onClick={() => {
                      setActiveImageModal(null);
                      onOpenConsultation();
                    }}
                    className="px-4 py-2 text-xs font-semibold text-white bg-[#0F766E] hover:bg-[#2A9D8F] rounded-xl transition-colors shadow-sm"
                  >
                    Schedule Visit
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
